import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../lib/api';
import { formatDateInTimezone } from '../utils/timezone';

interface Listing {
  _id: string;
  title: string;
  name: string;
  timezone: string;
}

interface Rental {
  _id: string;
  listingId: Listing;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  startDateTime: string;
  endDateTime: string;
  displayEndDateTime: string;
  price: number;
  renterName: string;
  renterEmail: string;
  renterPhone?: string;
  renterDetails: Record<string, string>;
  cancelledAt?: string;
  cancelledBy?: 'owner' | 'renter' | 'admin';
  cancellationReason?: string;
}

const ListingRentalsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchRentals = useCallback(async () => {
    try {
      const data = await api.get<{ rentals: Rental[] }>(`/listings/${id}/rentals`);
      setRentals(data.rentals);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to load rentals');
      navigate('/my-listings');
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  const handleCancelClick = (rental: Rental) => {
    setSelectedRental(rental);
    setCancellationReason('');
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedRental) return;

    if (cancellationReason.trim().length < 10) {
      toast.error('Cancellation reason must be at least 10 characters');
      return;
    }

    setIsCancelling(true);

    try {
      await api.post(`/rentals/${selectedRental._id}/cancel/owner`, {
        reason: cancellationReason.trim(),
      });
      toast.success('Rental cancelled successfully');
      setShowCancelModal(false);
      setSelectedRental(null);
      setCancellationReason('');
      fetchRentals();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to cancel rental');
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusBadgeClass = (status: Rental['status']) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const listingTitle = rentals.length > 0 ? rentals[0].listingId.title : 'Listing';

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <button
            onClick={() => navigate('/my-listings')}
            className="text-blue-600 hover:text-blue-800 mb-2 flex items-center"
          >
            ← Back to My Listings
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Rentals for {listingTitle}
          </h1>
        </div>
      </div>

      {rentals.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500 text-lg">No rentals found for this listing.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {rentals.map((rental) => (
            <div key={rental._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-semibold">{rental.renterName}</h2>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                        rental.status
                      )}`}
                    >
                      {rental.status.charAt(0).toUpperCase() + rental.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-600">{rental.renterEmail}</p>
                  {rental.renterPhone && (
                    <p className="text-gray-600">{rental.renterPhone}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    ${rental.price.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Start Date & Time</p>
                  <p className="font-medium">
                    {formatDateInTimezone(new Date(rental.startDateTime), rental.listingId.timezone)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">End Date & Time</p>
                  <p className="font-medium">
                    {formatDateInTimezone(new Date(rental.displayEndDateTime), rental.listingId.timezone)}
                  </p>
                </div>
              </div>

              {rental.renterDetails && Object.keys(rental.renterDetails).length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Additional Information</p>
                  <div className="bg-gray-50 p-3 rounded">
                    {Object.entries(rental.renterDetails).map(([key, value]) => (
                      <div key={key} className="mb-1">
                        <span className="font-medium">{key}:</span> {value}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {rental.status === 'cancelled' && rental.cancellationReason && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-1">Cancellation Reason</p>
                  <div className="bg-red-50 border border-red-200 p-3 rounded">
                    <p className="text-red-800">{rental.cancellationReason}</p>
                    {rental.cancelledBy && (
                      <p className="text-sm text-red-600 mt-1">
                        Cancelled by: {rental.cancelledBy}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {rental.status === 'confirmed' && (
                <div className="flex justify-end">
                  <button
                    onClick={() => handleCancelClick(rental)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Cancel Rental
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedRental && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">Cancel Rental</h2>
            <p className="text-gray-600 mb-4">
              You are about to cancel the rental for <strong>{selectedRental.renterName}</strong>.
            </p>
            <p className="text-gray-600 mb-4">
              Please provide a reason for the cancellation. This will be sent to the renter.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason *
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Enter reason for cancellation (minimum 10 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                disabled={isCancelling}
              />
              <p className="text-sm text-gray-500 mt-1">
                {cancellationReason.trim().length} / 10 characters minimum
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-4">
              <p className="text-sm text-yellow-800">
                The renter will receive a full refund automatically.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedRental(null);
                  setCancellationReason('');
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                disabled={isCancelling}
              >
                Back
              </button>
              <button
                onClick={handleCancelConfirm}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isCancelling || cancellationReason.trim().length < 10}
              >
                {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingRentalsPage;

